import { useQuery } from '@tanstack/react-query';
import { ChevronRight, LayoutGrid } from 'lucide-react';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { cn } from '@shared/lib/cn';
import { resolveAccessEntryTypeIcon } from './accessEntryTypeIcons';
import { fetchAccessEntryTypesForInvite, type MemberAccessEntryTypeOption } from './api';
import { ANY_TYPE, entryTypeRule } from './visitorAccessFormat';

interface EntryTypeListProps {
  /** The person's type: null is Any type, undefined is none chosen yet. */
  selectedId?: string | null;
  /** `chevron` moves on to the next step when tapped; `radio` marks the person's current type. */
  mark: 'chevron' | 'radio';
  /** Called with the type tapped, or null for Any type. */
  onSelect: (type: MemberAccessEntryTypeOption | null) => void;
}

/**
 * The kinds of visitor the member can give PINs to (the organisation's entry types), each with the
 * rules its PINs follow, after Any type: someone with no type of their own, who fits every gate.
 * Used to pick a new person's type and to change it later.
 */
export function EntryTypeList({ selectedId, mark, onSelect }: EntryTypeListProps): JSX.Element {
  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });

  if (typesQuery.isLoading) {
    return (
      <div className="flex flex-col gap-2.5">
        <Skeleton className="h-[4.25rem] rounded-lg" />
        <Skeleton className="h-[4.25rem] rounded-lg" />
      </div>
    );
  }

  if (typesQuery.isError) return <Banner variant="danger" title="Could not load visitor types" />;

  if (!typesQuery.data?.length) {
    return <Banner title="No visitor types available" description="Ask management to enable visitor entry types." />;
  }

  // Any type first, then the organisation's types. Only the radio marks the current one.
  const rows = [
    { id: null, ...ANY_TYPE, type: null },
    ...typesQuery.data.map((type) => ({ id: type.access_entry_type_id, name: type.name, rule: entryTypeRule(type), type })),
  ];

  return (
    <div className="flex flex-col gap-2.5">
      {rows.map((row) => {
        const selected = mark === 'radio' && row.id === selectedId;
        return (
          <button
            key={row.id ?? 'any'}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(row.type)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border bg-card px-3.5 py-3 text-left transition-colors',
              selected ? 'border-primary-600 ring-1 ring-primary-600' : 'border-hairline',
            )}
          >
            <EntryTypeGlyph type={row.type} />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
              <span className="text-[0.9375rem] font-semibold text-ink">{row.name}</span>
              <span className="text-caption text-ink-muted">{row.rule}</span>
            </span>
            {mark === 'chevron' ? (
              <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
            ) : (
              <span
                aria-hidden
                className={cn(
                  'h-5 w-5 shrink-0 rounded-pill border-2',
                  selected
                    ? 'border-primary-600 bg-primary-600 shadow-[inset_0_0_0_3px_var(--surface-card)]'
                    : 'border-hairline',
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** An entry type's icon on a soft rounded square, as the Visitor access design draws it; a grid for Any type. */
export function EntryTypeGlyph({ type }: { type?: MemberAccessEntryTypeOption | null }): JSX.Element {
  const Icon = type ? resolveAccessEntryTypeIcon(type) : LayoutGrid;
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
  );
}
